/**
 * Foto principal de la ficha, con miniaturas cuando el producto tiene mas de
 * una (ADM-04).
 *
 * La principal es la imagen mas grande de la vista y por lo tanto el LCP: se
 * carga sin diferir, sin importar cual de las fotos este seleccionada. Elegir
 * una miniatura solo cambia cual imagen ya cargada se ve grande — no hay
 * pedido de red de por medio, las fotos ya vinieron todas en `producto.imagenes`.
 *
 * Con el mouse encima, la foto se ve ampliada en un panel al costado
 * (`useZoomFoto`), que reemplaza al leve agrandado que tenia antes.
 *
 * Los videos van en la misma fila de miniaturas, despues de las fotos, con un
 * cuadro del propio video y el boton de play encima (`MiniaturaVideo`). La
 * primera foto sigue siendo la principal y el LCP, y cada video se reproduce
 * solo si se elige (`VideoFicha`). Con un video a la vista no hay lupa. Las
 * miniaturas que no caben en una linea bajan a la siguiente: nunca hay
 * desplazamiento lateral.
 *
 * En el telefono se pasa de foto deslizando el dedo sobre ella, y despues de
 * la ultima foto vienen los videos. No da la vuelta: al final, seguir
 * deslizando no hace nada, como en una galeria de fotos del propio telefono.
 */
import { useCallback, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import type { ImagenProducto, VideoProducto } from '@bandu/compartido';

import { useAltoObservado } from '../../hooks/use-alto-observado';
import { useDeslizamiento } from '../../hooks/use-deslizamiento';
import { useZoomFoto } from '../../hooks/use-zoom-foto';
import { FotoProducto, Imagen } from '../comunes/Imagen';
import { MiniaturaVideo } from './MiniaturaVideo';
import { VideoFicha } from './VideoFicha';

export interface GaleriaProductoProps {
  imagenes: ImagenProducto[];
  videos?: VideoProducto[];
  nombre: string;
  /** Etiquetas que van sobre la foto: oferta, sin stock. */
  etiquetas?: ReactNode;
}

export function GaleriaProducto({
  imagenes,
  videos = [],
  nombre,
  etiquetas,
}: GaleriaProductoProps) {
  const [indice, setIndice] = useState(0);
  // Un solo recorrido: primero las fotos y despues los videos.
  const total = imagenes.length + videos.length;
  const video = indice >= imagenes.length ? (videos[indice - imagenes.length] ?? null) : null;
  const viendoVideo = video !== null;
  const principal = imagenes[indice] ?? imagenes[0] ?? null;

  const variasFotos = total > 1;
  const { zoom: zoomFoto, mover, salir } = useZoomFoto();
  const zoom = viendoVideo ? null : zoomFoto;

  const pasarFoto = useCallback(
    (salto: 1 | -1): void => {
      setIndice((actual) => Math.min(Math.max(actual + salto, 0), total - 1));
    },
    [total],
  );
  const deslizamiento = useDeslizamiento(pasarFoto);
  // Las miniaturas bajan a otra linea cuando no caben: la foto se acorta lo
  // que midan, para que todas sigan cabiendo en la ventana (`ficha.css`).
  const [refMiniaturas, altoMiniaturas] = useAltoObservado<HTMLElement>();

  return (
    <div
      className="ficha-galeria"
      style={
        altoMiniaturas !== null
          ? ({ '--alto-miniaturas': `${String(altoMiniaturas)}px` } as CSSProperties)
          : undefined
      }
    >
      {variasFotos ? (
        <nav
          ref={refMiniaturas}
          className="ficha-rail-miniaturas"
          aria-label={videos.length > 0 ? `Fotos y videos de ${nombre}` : `Fotos de ${nombre}`}
        >
          <ul className="ficha-miniaturas">
            {imagenes.map((imagen, posicion) => (
              <li key={`foto-${String(imagen.id)}`}>
                <button
                  type="button"
                  className={posicion === indice ? 'ficha-miniatura activa' : 'ficha-miniatura'}
                  aria-pressed={posicion === indice}
                  aria-label={`Ver foto ${posicion + 1} de ${imagenes.length}`}
                  onClick={() => {
                    setIndice(posicion);
                  }}
                >
                  <Imagen src={imagen.url} alt="" ancho={imagen.ancho} alto={imagen.alto} />
                </button>
              </li>
            ))}
            {videos.map((elemento, posicion) => {
              const lugar = imagenes.length + posicion;
              return (
                <li key={`video-${String(elemento.id)}`}>
                  <button
                    type="button"
                    className={
                      lugar === indice
                        ? 'ficha-miniatura miniatura-video activa'
                        : 'ficha-miniatura miniatura-video'
                    }
                    aria-pressed={lugar === indice}
                    aria-label={`Ver video ${posicion + 1} de ${videos.length}`}
                    onClick={() => {
                      setIndice(lugar);
                    }}
                  >
                    <MiniaturaVideo video={elemento} />
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      ) : null}

      <div className="ficha-foto-envoltura">
        <figure
          className={viendoVideo ? 'ficha-foto con-video' : 'ficha-foto'}
          onPointerEnter={mover}
          onPointerMove={mover}
          onPointerLeave={salir}
          {...(variasFotos ? deslizamiento : {})}
        >
          {etiquetas !== undefined ? <p className="ficha-etqs">{etiquetas}</p> : null}
          {video !== null ? (
            <VideoFicha key={video.id} video={video} nombre={nombre} />
          ) : (
            <FotoProducto imagen={principal} nombre={nombre} prioridad />
          )}
          {imagenes.length > 1 && !viendoVideo ? (
            <span className="ficha-posicion" aria-hidden="true">
              {indice + 1} / {imagenes.length}
            </span>
          ) : null}
          {zoom !== null ? (
            <span
              className="ficha-lente"
              aria-hidden="true"
              style={{
                left: zoom.lente.x,
                top: zoom.lente.y,
                width: zoom.lente.ancho,
                height: zoom.lente.alto,
              }}
            />
          ) : null}
        </figure>
        {/* La foto ampliada es decorativa: repite la que ya tiene su alt, asi
            que va como fondo y fuera del arbol de accesibilidad. */}
        {zoom !== null && principal !== null ? (
          <div
            className="ficha-zoom"
            aria-hidden="true"
            style={{
              width: zoom.caja.ancho,
              height: zoom.caja.alto,
              backgroundImage: `url("${principal.url}")`,
              backgroundSize: `${zoom.fondo.ancho}px ${zoom.fondo.alto}px`,
              backgroundPosition: `${zoom.fondo.x}px ${zoom.fondo.y}px`,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
